import Papa from 'papaparse';

export const MAX_CSV_SIZE = 5 * 1024 * 1024;

// MIME type browsers report for a modern .xlsx workbook. Kept as a single named
// constant because the same ~70-char string is needed by the file-acceptance
// gate and the upload content-type map, and a typo in either copy would be
// silent.
export const SPREADSHEET_MIME =
	'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

// What the two contact-import file pickers accept. Both extensions and MIME
// types are listed so click-to-browse and drag-and-drop agree (some browsers
// match on extension, others on type).
export const CONTACTS_ACCEPTED_FILE_TYPES = [
	'.csv',
	'.xlsx',
	'text/csv',
	SPREADSHEET_MIME,
];
export const CONTACTS_FILE_INPUT_ACCEPT =
	CONTACTS_ACCEPTED_FILE_TYPES.join(',');

function isXlsxFile(file: File): boolean {
	// The filename is the primary signal: some browsers report an empty
	// file.type for an .xlsx, so the MIME check is only a backstop.
	return /\.xlsx$/i.test(file.name) || file.type === SPREADSHEET_MIME;
}

export type ContactsFileReadResult = {
	csv: string;
	// Names of any sheets after the first, which the converter does not read.
	// Empty for a .csv and for a single-sheet workbook; callers surface it so a
	// user whose contacts live on a second tab isn't silently short-imported.
	ignoredSheetNames: string[];
};

/**
 * Read a picked contact file as CSV text. A .csv is read verbatim; an .xlsx is
 * converted by reading the first sheet with read-excel-file (lightweight,
 * browser-native) and serialising the rows to CSV via Papa.unparse().
 */
export async function readContactsFileAsCsv(
	file: File,
): Promise<ContactsFileReadResult> {
	if (!isXlsxFile(file)) {
		return { csv: await file.text(), ignoredSheetNames: [] };
	}
	const readExcelFile = (await import('read-excel-file/browser')).default;
	const sheets = await readExcelFile(file);
	if (sheets.length === 0) return { csv: '', ignoredSheetNames: [] };
	const [first, ...rest] = sheets;
	const ignoredSheetNames = rest.map((s) => s.sheet);
	if (first.data.length === 0) return { csv: '', ignoredSheetNames };
	const csv = Papa.unparse(
		first.data.map((row) =>
			row.map((cell) => (cell == null ? '' : String(cell))),
		),
	);
	return { csv, ignoredSheetNames };
}

/**
 * A non-blocking notice when an imported workbook had sheets beyond the first.
 * Returns null when nothing was skipped. Shared so both import surfaces show
 * identical copy.
 */
export function ignoredSheetsNotice(
	ignoredSheetNames: string[],
): string | null {
	const count = ignoredSheetNames.length;
	if (count === 0) return null;
	return (
		`Only the first sheet was imported. ${count} other ` +
		`sheet${count === 1 ? '' : 's'} (${ignoredSheetNames.join(', ')}) ` +
		`${count === 1 ? 'was' : 'were'} skipped.`
	);
}

export const EMAIL_REGEX =
	/^[A-Za-z0-9](?:[A-Za-z0-9._%+-]*[A-Za-z0-9])?@[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?)*\.[A-Za-z]{2,}$/;

export function isValidEmail(value: string): boolean {
	return EMAIL_REGEX.test(value);
}

export function isValidPhone(value: string): boolean {
	const digits = value.replace(/\D/g, '');
	return (
		digits.length === 10 || (digits.length === 11 && digits.startsWith('1'))
	);
}

export function isValidInstagram(value: string): boolean {
	const handle = value.trim().replace(/^@/, '');
	return /^[a-zA-Z0-9._]{1,30}$/.test(handle);
}

function parseRows(text: string): string[][] {
	return Papa.parse<string[]>(text, { skipEmptyLines: 'greedy' }).data;
}

function valueAt(fields: string[], index: number): string {
	return index >= 0 ? (fields[index]?.trim() ?? '') : '';
}

function findColumn(headers: string[], candidates: string[]): number {
	for (const candidate of candidates) {
		const index = headers.indexOf(candidate);
		if (index >= 0) return index;
	}
	return -1;
}

export type ParsedCsvRecipient = {
	name: string;
	email: string;
};

export function parseCsvText(text: string): {
	recipients: ParsedCsvRecipient[];
	error: string | null;
} {
	const rows = parseRows(text);
	if (rows.length === 0) {
		return { recipients: [], error: 'CSV file is empty' };
	}

	const headers = rows[0].map((header) => header.trim().toLowerCase());
	const nameIndex = headers.indexOf('name');
	const emailIndex = headers.indexOf('email');
	if (nameIndex < 0 || emailIndex < 0) {
		return { recipients: [], error: 'Missing required columns: name, email' };
	}

	const recipients = rows
		.slice(1)
		.map((fields) => ({
			name: valueAt(fields, nameIndex),
			email: valueAt(fields, emailIndex),
		}))
		.filter(
			(recipient) => recipient.email && EMAIL_REGEX.test(recipient.email),
		);

	if (recipients.length === 0) {
		return { recipients: [], error: 'CSV contains no valid recipients' };
	}

	return { recipients, error: null };
}

const NAME_HEADERS = ['name', 'full name', 'full_name'];
const FIRST_NAME_HEADERS = ['first name', 'first_name'];
const LAST_NAME_HEADERS = ['last name', 'last_name'];
const EMAIL_HEADERS = ['email', 'e-mail', 'email address', 'email_address'];
const PHONE_HEADERS = ['phone', 'phone number', 'phone_number'];
const INSTAGRAM_HEADERS = [
	'instagram',
	'instagram handle',
	'instagram_handle',
	'ig',
];

export type ParsedContactRow = {
	name?: string;
	email?: string;
	phone?: string;
	instagram_handle?: string;
};

export function parseContactsCsv(text: string): {
	contacts: ParsedContactRow[];
	error: string | null;
} {
	const rows = parseRows(text);
	if (rows.length === 0) {
		return { contacts: [], error: 'CSV file is empty' };
	}

	const headers = rows[0].map((header) => header.trim().toLowerCase());
	const nameIndex = findColumn(headers, NAME_HEADERS);
	const firstNameIndex = findColumn(headers, FIRST_NAME_HEADERS);
	const lastNameIndex = findColumn(headers, LAST_NAME_HEADERS);
	const emailIndex = findColumn(headers, EMAIL_HEADERS);
	const phoneIndex = findColumn(headers, PHONE_HEADERS);
	const instagramIndex = findColumn(headers, INSTAGRAM_HEADERS);

	if (emailIndex < 0 && phoneIndex < 0 && instagramIndex < 0) {
		return {
			contacts: [],
			error:
				'Missing columns: include at least one of email, phone, or instagram',
		};
	}

	const contacts = rows
		.slice(1)
		.map((fields) => {
			const row: ParsedContactRow = {};

			let name = valueAt(fields, nameIndex);
			if (!name) {
				name = [valueAt(fields, firstNameIndex), valueAt(fields, lastNameIndex)]
					.filter(Boolean)
					.join(' ');
			}
			if (name) row.name = name;

			const email = valueAt(fields, emailIndex);
			if (email && isValidEmail(email)) row.email = email;

			const phone = valueAt(fields, phoneIndex);
			if (phone && isValidPhone(phone)) row.phone = phone;

			const instagram = valueAt(fields, instagramIndex).replace(/^@/, '');
			if (instagram && isValidInstagram(instagram)) {
				row.instagram_handle = instagram;
			}

			return row;
		})
		.filter((row) => row.email || row.phone || row.instagram_handle);

	if (contacts.length === 0) {
		return { contacts: [], error: 'CSV contains no valid contacts' };
	}

	return { contacts, error: null };
}
